import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { useSnackbar } from 'notistack'
import {
  deleteData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../../../apis/api.helpers'
import apiUrl from '../../../../apis/api.url'
import { QueryParams } from '../../../../common/types'
import { useSnackbarManager } from '../../../../components/common/snackbar'
import {
  getErrorMessage,
  parseQueryParams,
} from '../../../../utilities/parsers'
import { useAuthStore } from '../../../../store/authStore'

const buildUrlWithParams = (baseUrl: string, params: QueryParams) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_INSTITUTIONS, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useAdminUser = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchData(input), {})
}

const customerMode = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.CUSTOMER_MODE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useCustomerMode = (input: QueryParams) => {
  return useQuery(['customer_mode', input], () => customerMode(input), {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_INSTITUTIONS}/${id}`)
}

const adminDetails = async (id: string) => {
  const url = `${apiUrl.ADMIN_INSTITUTIONS}/${id}`
  const response = await getData(url)
  return response
}
export const useAdminDetails = (id: string) => {
  return useQuery(['worker_task', id], () => adminDetails(id), {
    refetchOnWindowFocus: false,
  })
}

export const createAdmin = (input: any) => {
  return postData(`${apiUrl.CREATE_INTERNSHIP}`, input)
}
export const useCreateAdmin = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(createAdmin, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Internship application submitted', {
        variant: 'success',
      })
    },

    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const assignTemplate = (input: any) => {
  return postData(`${apiUrl.TEMPLATE_ASSIGN}`, input)
}
export const useAssignTemplate = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(assignTemplate, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Template Assigned Successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const editCategory = (data: { id: number; input: any }) => {
  return updateData(`${apiUrl.ADMIN_INSTITUTIONS}/${data.id}`, {
    user: data.input,
  })
}

export const useUpdateAdmin = (
  handleSubmission: (data: any) => void // Simplify this if `id` isn't needed separately
) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(editCategory, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)

      enqueueSnackbar('Customer Edited Successfully', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}
const fetchInternshipsUser = async () => {
  const url = buildUrlWithParams(apiUrl.INTERNSHIP_LIST_USER, { page: 1 })
  const response = await getData(url)
  return response
}
const fetchInternships = async () => {
  const url = buildUrlWithParams(apiUrl.INTERNSHIP_LIST_COMMON, { page: 1 })
  const response = await getData(url)
  return response
}
export const useInternshipList = () => {
  const authenticated = useAuthStore((state) => state.authenticated)

  return useQuery(
    ['internship_listing', authenticated],
    () => {
      if (authenticated) {
        return fetchInternshipsUser()
      } else {
        return fetchInternships()
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  )
}
const fetchUserData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.PROFILE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useFetchUserData = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchUserData(input), {})
}
